/**
 * useStep2AssetIdentification — Step 2 lifecycle.
 *
 * Composes three sub-flow hooks into the Step 2 body-props contract:
 *   - `useStructuredFetch`     — asset list from `getAgentResult`
 *   - `useRecordCRUD`           — add / update / delete with cascade
 *   - `useHierarchicalApprove`  — child-TARA relevance modifications +
 *     `approveHierarchicalData` for the hierarchical Approve button
 *
 * Hierarchical mode is gated by the session's `analysis_mode` field
 * (plumbed `ProjectWorkspace → WorkflowV2 → here`). When active, the
 * hook surfaces `approveOverride` so `WorkflowV2.tsx`'s global Approve
 * button routes through `approveHierarchicalData` rather than the
 * default `useApproveFlow.onApprove → resumeWorkflow`.
 */
import { useCallback } from 'react';
import { getAgentResult } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import { useHierarchicalApprove } from '../hooks/useHierarchicalApprove';
import type { Step2BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'asset_identification_agent';

/**
 * Wire shape from `GET /workflow/{tid}/agent/{name}/result`:
 *
 *   { thread_id, agent_name, status, result: { ...agent output... },
 *     progress_percentage, started_at, completed_at, error_message }
 *
 * The agent's actual payload is on the inner `result` object, which
 * exposes `assets` (or sometimes `identified_assets`).
 */
interface AgentResultResponse {
  thread_id?: string;
  agent_name?: string;
  status?: string;
  result?: {
    assets?: unknown[];
    identified_assets?: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface UseStep2Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  /** True when the whole TARA has finished — widens the edit window
   *  to all agent-backed steps (mirrors legacy WorkflowStatus === COMPLETED). */
  workflowCompleted: boolean;
  /** Session-level `analysis_mode`. `'hierarchical'` flips Step 2 to
   *  the Current TARA / Child TARAs two-tab review even when the
   *  child-TARA asset list is empty (the tab is shown so the user
   *  knows inherited rows are expected). */
  analysisMode?: string | null;
  /** Reducer state — needed so `useHierarchicalApprove` can read
   *  `state.steps[2].hierarchicalModifications` (Story H2). */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch. */
  actions: WorkflowActions;
}

export function useStep2AssetIdentification({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep2Input): Step2BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);

  // `getAgentResult` is JS — its return type isn't typed for TS
  // consumers. Cast through `unknown`.
  const fetcher = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  // Real payload lives at `raw.result.assets` (some older agents
  // use `identified_assets` — fall through to it).
  const extract = useCallback((raw: AgentResultResponse | null): unknown[] => {
    const r = raw?.result;
    return (r?.assets ?? r?.identified_assets ?? []) as unknown[];
  }, []);

  const structured = useStructuredFetch<AgentResultResponse | null, unknown[]>({
    fetcher,
    extract,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  // Legacy edit-permission rule (utils/editPermissions.canEditAgent):
  //   - workflowCompleted → all steps editable (except 1 and 9)
  //   - otherwise → only the step currently `draft` (awaiting review)
  // `revising` (agent re-running) is intentionally NOT editable — the
  // agent is about to overwrite the records.
  const editable = workflowCompleted || status === 'draft';
  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  // Hierarchical mode — session-level. Composes the shared hook so
  // Stories 3-8 can drop in with their own agent name + primary key
  // without duplicating the modifications + approve closure.
  const hierarchicalEnabled = analysisMode === 'hierarchical';
  const records = (structured.data ?? []) as Array<Record<string, unknown>>;
  const hierApprove = useHierarchicalApprove({
    stepId: 2,
    threadId,
    agentName: AGENT_NAME,
    enabled: hierarchicalEnabled,
    records,
    primaryKey: 'asset_id',
    state,
    actions,
    onApproved: structured.refetch,
  });

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    crud,
    hierarchical: {
      enabled: hierarchicalEnabled,
      modifications: hierApprove.modifications,
      setRelevance: hierApprove.setRelevance,
      setJustification: hierApprove.setJustification,
      onApprove: hierApprove.onApprove,
      approving: hierApprove.approving,
      approveError: hierApprove.approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: hierApprove.onApprove, approving: hierApprove.approving }
      : undefined,
  };
}
